import { useMutation, useQuery } from "@apollo/client/react";
import {
  CreateRawItemDocument,
  DeleteRawItemDocument,
  MyRawItemsDocument,
  MyStoreDocument,
  UpdateRawItemDocument,
} from "@/generated/graphql";
import { graphQLErrorMessage } from "@/lib/graphqlErrors";
import type { RawItem, RawItemInput } from "@/types/models";

export function useRawItems() {
  const { data: storeData, loading: storeLoading } = useQuery(MyStoreDocument, {
    fetchPolicy: "network-only",
  });

  const { data, loading, error, refetch } = useQuery(MyRawItemsDocument, {
    fetchPolicy: "network-only",
    skip: !storeData?.myStore,
  });

  const rawItems: RawItem[] = data?.myRawItems ?? [];
  const store = storeData?.myStore ?? null;

  const [createRawItemMutation, { loading: creating }] = useMutation(CreateRawItemDocument, {
    refetchQueries: [{ query: MyRawItemsDocument }],
  });

  const [updateRawItemMutation, { loading: updating }] = useMutation(UpdateRawItemDocument, {
    refetchQueries: [{ query: MyRawItemsDocument }],
  });

  const [deleteRawItemMutation, { loading: deleting }] = useMutation(DeleteRawItemDocument, {
    refetchQueries: [{ query: MyRawItemsDocument }],
  });

  const createRawItem = (
    input: RawItemInput,
    callbacks?: { onCompleted?: () => void; onError?: (message: string) => void }
  ) => {
    createRawItemMutation({
      variables: { input },
      onCompleted: () => callbacks?.onCompleted?.(),
      onError: (err) => {
        callbacks?.onError?.(graphQLErrorMessage(err, "Não foi possível criar o insumo."));
      },
    });
  };

  const updateRawItem = (
    input: RawItemInput,
    callbacks?: { onCompleted?: () => void; onError?: (message: string) => void }
  ) => {
    updateRawItemMutation({
      variables: { input },
      onCompleted: () => callbacks?.onCompleted?.(),
      onError: (err) => {
        callbacks?.onError?.(graphQLErrorMessage(err, "Não foi possível atualizar o insumo."));
      },
    });
  };

  const removeRawItem = (
    id: string,
    callbacks?: { onCompleted?: () => void; onError?: (message: string) => void }
  ) => {
    deleteRawItemMutation({
      variables: { id },
      onCompleted: () => callbacks?.onCompleted?.(),
      onError: (err) => {
        callbacks?.onError?.(graphQLErrorMessage(err, "Não foi possível excluir o insumo."));
      },
    });
  };

  return {
    store,
    rawItems,
    loading: storeLoading || loading,
    error,
    refetch,
    createRawItem,
    updateRawItem,
    removeRawItem,
    saving: creating || updating,
    deleting,
  };
}
